import { Box, Skeleton, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import {
  Navigate,
  useNavigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import { z } from 'zod';

import { utils as commonUtils } from '@finley-technologies/common';

import { FundingRequestDetailViewContainer } from 'borrower/domains/fundingRequests/ui/detail/ViewContainer';
import { FundingRequestsProvider } from 'borrower/domains/fundingRequests/ui/manage/Context';
import {
  isFundingRequestManageStep,
  FundingRequestManageStep,
  FundingRequestManageViewContainer,
} from 'borrower/domains/fundingRequests/ui/manage/ViewContainer';
import { FundingRequestsViewTemplate } from 'borrower/domains/fundingRequests/ui/ViewTemplate';
import {
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { useGetFundingRequestQuery } from 'packages/funding-requests/requests/requests.gql.generated';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

import 'source/borrower/borrower.scss';

// Types
const RouteParamsStruct = z.object({
  fundingRequestId: z.string({ message: 'Missing URL parameter' }),
});

// Helpers
/**
 * Converts hashFrangment to disired management step
 */
const getFundingRequestDesiredStep = (
  hashFragmentsString: string,
): FundingRequestManageStep | null => {
  if (_.isEmpty(hashFragmentsString)) {
    return null;
  }
  const fragmentString = _.trimStart(hashFragmentsString, '#');
  return isFundingRequestManageStep(fragmentString)
    ? (fragmentString as FundingRequestManageStep)
    : null;
};

// Exports
export const FundingRequestsRoute = () => {
  const params = useParams();
  const { fundingRequestId } = RouteParamsStruct.parse(params);
  const location = useLocation();
  const toast = useToast();
  const navigate = useNavigate();
  const [isCurrent, setIsCurrent] = useState(false);
  const { user } = useUserAuthOrThrow();

  const {
    data: fundingRequestData,
    loading: fundingRequestDataLoading,
    error,
  } = useGetFundingRequestQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: { fundingRequestId },
  });

  useEffect(() => {
    if (fundingRequestData) {
      const {
        fundingRequestWithFullDetails: { isCurrent: _isCurrent },
      } = fundingRequestData;
      setIsCurrent(!!_isCurrent);
    }
  }, [fundingRequestData]);

  if (!fundingRequestData || fundingRequestDataLoading) {
    return (
      <Skeleton height="600px" data-testid="draw-request-id-route-loading" />
    );
  }

  if (error) {
    return <Navigate to="/funding-requests" />;
  }

  const desiredStep = getFundingRequestDesiredStep(location.hash);

  let routeLevelError = false;
  return (
    <Box className="main">
      <Sentry.ErrorBoundary
        onError={() => {
          if (!routeLevelError && !toast.isActive(errorToastId)) {
            errorToastConfig.title = `Failed to load draw requests page`;
            routeLevelError = true;
            toast(errorToastConfig);
            navigate('/funding-requests');
          }
        }}
      >
        {isCurrent ? (
          <FundingRequestsViewTemplate
            fundingRequestId={fundingRequestId}
            isCreditFacilitySelectDisabled
            selectedTab="manage"
          >
            <FundingRequestsProvider
              borrowerKey={user.organization.slug}
              fundingRequestId={fundingRequestId}
            >
              <FundingRequestManageViewContainer
                desiredStepDefault={desiredStep}
              />
            </FundingRequestsProvider>
          </FundingRequestsViewTemplate>
        ) : (
          <FundingRequestDetailViewContainer
            fundingRequestId={fundingRequestId}
            creditFacilityId={
              fundingRequestData.fundingRequestWithFullDetails.creditFacilityId
            }
          />
        )}
      </Sentry.ErrorBoundary>
    </Box>
  );
};
